<script>
import debounce from 'lodash.debounce';
import { mapGetters, mapState } from 'vuex';
import FormSelections from './FormSelections.vue';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator.vue';
import SelectOptions from '../SelectOptions/SelectOptions.vue';
import FormFilters from './FormFilters.vue';
import config from './config';
import t from './state/types';

export default {
  name: 'FormFinder',
  components: {
    FormFilters,
    FormSelections,
    LoadingIndicator,
    SelectOptions,
  },
  props: {
    eventId: {
      type: String,
      default: null,
    },
    eventRunners: {
      type: String,
      default: null,
    },
    eventStatus: {
      type: String,
      default: t.EVENT_ENABLED,
    },
    shortlist: {
      type: Array,
      default: () => [],
    },
  },
  data: function() {
    return {
      showInfoDisplay: false,
    };
  },
  computed: {
    ...mapGetters({
      categories: 'FormFinder/categories',
      currentDateFilter: 'FormFinder/dateFilter',
      currentPreset: 'FormFinder/currentPreset',
      currentSort: 'FormFinder/currentSort',
      dateSortOptions: 'FormFinder/dateSortOptions',
      dayRange: 'FormFinder/dayRange',
      count: 'FormFinder/count',
      events: 'FormFinder/events',
      featuredPresets: 'FormFinder/featuredPresets',
      fields: 'FormFinder/fields',
      fieldDescriptions: 'FormFinder/fieldDescriptions',
      groups: 'FormFinder/groups',
      metrics: 'FormFinder/metrics',
      presets: 'FormFinder/presets',
      runnerCount: 'FormFinder/runnerCount',
      sort: 'FormFinder/sort',
    }),
    ...mapState({
      hash: state => state.FormFinder.hash,
      status: state => state.FormFinder.status,
      info: state => state.FormFinder.info,
    }),
    fetchError() {
      return this.status === t.ERROR_FETCH;
    },
    fetching() {
      return this.status === t.FETCH_RESULTS;
    },
    hasInfo() {
      return this.fieldDescriptions.length;
    },
    noResults() {
      return this.status === t.RESULTS_NONE;
    },
    ok() {
      return this.eventStatus === t.EVENT_ENABLED && this.status !== t.ERROR_INIT;
    },
    eventDisabled() {
      return this.eventStatus === t.EVENT_DISABLED;
    },
    eventNotReady() {
      return this.eventStatus === t.EVENT_NOT_READY;
    },
    presetOptions() {
      return this.presets.map(preset => ({
        label: preset.displayName,
        value: preset.name,
      }));
    },
    results() {
      return this.status === t.RESULTS;
    },
    shortlisted() {
      return this.shortlist.map(selection => Number(selection.item));
    },
  },
  watch: {
    $route(to) {
      if (to.params.hasOwnProperty('hash') && to.params.hash !== this.hash) {
        this.searchHash(to.params.hash);
      }
    },
  },
  mounted() {
    let hash = null;

    if (this.$route) {
      hash = this.$route && this.$route.name === t.ROUTE_SEARCH ? this.$route.params.hash : null;
    }

    this.$store.dispatch('FormFinder/init', [hash, this.eventId, this.eventRunners]);
  },
  methods: {
    displayRunnerCard: function(selectionId) {
      if (this.$ga) this.$ga.event('Form Finder', 'Navigated to Runner Card', 'Selection', selectionId);
      this.$emit('displayRunnerCard', selectionId);
    },
    filterChange: function(filter, metric, value, range) {
      if (this.$ga) this.$ga.event('Form Finder', 'Modified Settings', metric);

      this.$store.dispatch('FormFinder/updateFilter', [filter, metric, value, range]);
      this.$store.commit(t.STATUS, t.FETCH_RESULTS);
      this.search();
    },
    changePreset: function(preset) {
      if (preset === '' || (this.currentPreset && this.currentPreset === preset)) {
        return;
      }

      if (this.$ga) this.$ga.event('Form Finder', 'Selected Preset', preset);
      this.$store.commit(t.RESET);
      this.$store.commit(t.PRESET, preset);

      this.$store.dispatch('FormFinder/search').then(newHash => {
        if (typeof this.$router !== 'undefined') {
          this.$router.push({ name: t.ROUTE_SEARCH, params: { hash: newHash } });
        }
      });
    },
    changeSort: function(sort) {
      if (this.currentSort !== sort) {
        this.$store.commit(t.SORT, sort);
      }
    },
    dateFilterChange: function(filter) {
      if (this.currentDateFilter !== filter) {
        this.$store.commit(t.DATE_FILTER, filter);
      }
    },
    reset: function() {
      this.$store.commit('RESET');
    },
    search: debounce(function() {
      this.$store.dispatch('FormFinder/search').then(hash => {
        if (typeof this.$router !== 'undefined') {
          this.$router.push({ name: t.ROUTE_SEARCH, params: { hash } });
        }
      });
    }, config.requestDelay),
    searchHash: function(hash) {
      this.$store.dispatch('FormFinder/search', hash).then(newHash => {
        if (typeof this.$router !== 'undefined') {
          this.$router.push({ name: t.ROUTE_SEARCH, params: { hash: newHash } });
        }
      });
    },
    shortlistSelection: function(selectionId, method = 'add') {
      if (this.$ga) this.$ga.event('Form Finder', 'Runner Shortlisted', 'Selection', selectionId);
      this.$emit('shortlistSelection', selectionId, method);
    },
    toggleInfoDisplay() {
      this.showInfoDisplay = !this.showInfoDisplay;
    },
  },
};
</script>

<template>
  <div>
    <div v-if="ok" class="form-finder">
      <div class="form-finder__container">
        <div class="form-finder__parameters">
          <SelectOptions
            :options="presetOptions"
            :value="currentPreset"
            class="presets"
            @input="changePreset"
          >
            Select a preset
          </SelectOptions>
          <dl class="form-finder__preset-info">
            <dt>Preset description</dt>
            <dd><i></i>We've created a number of presets to get you started. Form Factor Specialist is our favourite. It finds all runners with a distance, track, track/distance, and track condition place strike rate of 50% or more. Give it a go!</dd>
          </dl>
          <SelectOptions
            :options="sort"
            :value="currentSort"
            class="sort"
            @input="changeSort"
          >
            Sort by
          </SelectOptions>
        </div>
        <div class="form-finder__settings-container">
          <h2>Set strike rates using the form filters below</h2>
          <FormFilters
            :categories="categories"
            :groups="groups"
            :settings="fields"
            :metrics="metrics"
            :runner-count="runnerCount"
            :status="status"
            :events="events"
            :date-sort-options="dateSortOptions"
            :total-count="count"
            @reset="reset"
            @filterChange="filterChange"
            @dateFilterChange="dateFilterChange"
          />
        </div>
        <div v-if="fetching" class="form-finder__results-msg">
          <div class="form-finder__results-msg__content">
            <LoadingIndicator />
          </div>
        </div>
        <FormSelections v-else-if="results"
                        :events="events"
                        :sort="currentSort"
                        :shortlist="shortlisted"
                        @displayRunnerCard="displayRunnerCard"
                        @shortlistSelection="shortlistSelection" />
        <div v-else-if="noResults || fetchError" class="form-finder__results-msg form-finder__results-msg-info">
          <div class="form-finder__results-msg__content">
            <h3 v-if="count">There are <span>0</span> matching runners over the next {{ dayRange }} days.</h3>
            <p>Form Finder gives you the ability to narrow the field and find a winner.</p>
            <div v-if="featuredPresets.length" class="presets">
              <button v-for="featuredPreset in featuredPresets" :key="featuredPreset.value" @click="changePreset(featuredPreset.value)">{{ featuredPreset.label }}</button>
            </div>
          </div>
        </div>
        <div v-else class="form-finder__results-msg form-finder__results-msg-info">
          <div class="form-finder__results-msg__content">
            <h3 v-if="eventRunners">There are <span>{{ eventRunners }}</span> horses running in this race.</h3>
            <h3 v-else-if="count">There are <span>{{ count }}</span> runners available over the next {{ dayRange }} days.</h3>
            <p>Form Finder gives you the ability to narrow the field and find a winner.</p>
            <div v-if="featuredPresets.length" class="presets">
              <button v-for="featuredPreset in featuredPresets" :key="featuredPreset.value" @click="changePreset(featuredPreset.value)">{{ featuredPreset.label }}</button>
            </div>
          </div>
        </div>
      </div>
      <div class="form-finder__info-text">
        <h2 v-if="info && info.faq">{{ info.faq[0].question }}</h2>
        <pre v-if="info && info.faq">{{ info.faq[0].answer }}</pre>
        <h3 :class="{active: showInfoDisplay}" @click="toggleInfoDisplay()">Strike Rate Filters <svg width="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M12.682 4.018c.3 0 .5.1.7.3.4.4.4 1 0 1.4l-4.5 4.5 4.5 4.5c.4.4.4 1 0 1.4-.4.4-1 .4-1.4 0l-6-6 6-6c.2 0 .4-.1.7-.1" fill="#333732"/></svg></h3>
        <div v-if="hasInfo" v-show="showInfoDisplay">
          <div v-for="({label, description}) in fieldDescriptions" :key="label">
            <h4>{{ label }}</h4>
            <p>{{ description }}</p>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="eventNotReady" class="form-finder">
      <div class="form-finder__container">
        <div class="form-finder__results-msg form-finder__results-msg__disabled">
          <div class="form-finder__results-msg__content">
            <h3>Unavailable</h3>
            <p>Form Finder is currently unavailable. Please check back soon.</p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="form-finder">
      <div class="form-finder__container">
        <div class="form-finder__results-msg form-finder__results-msg__disabled">
          <div class="form-finder__results-msg__content">
            <h3>Unavailable</h3>
            <p>Form Finder is unavailable.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
